import { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { ShieldCheck, Filter, Activity, Download, ChevronLeft, ChevronRight, X, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

const ACTION_TONE = {
  auth_login:            "pm-pill-green",
  auth_logout:           "pm-pill-slate",
  document_view:         "pm-pill-blue",
  document_download:     "pm-pill-amber",
  create_brief:          "pm-pill-blue",
  submit_candidate:      "pm-pill-blue",
  decide_submission:     "pm-pill-amber",
  request_tactical_fit:  "pm-pill-blue",
  assess_tactical_fit:   "pm-pill-green",
  request_medical:       "pm-pill-blue",
  update_medical:        "pm-pill-amber",
  report_injury:         "pm-pill-amber",
  auto_upgrade_slot:     "pm-pill-red",
  comment:               "pm-pill-slate",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const DEFAULT_FILTERS = {
  page: 1, page_size: 50, actor: "", actor_role: "", action: "", record_type: "", date_from: "", date_to: "", q: "",
};

export default function AuditLog() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50, total_pages: 1, facets: { actions: [], roles: [] } });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const load = useCallback(() => {
    const params = Object.entries(filters)
      .filter(([, v]) => v !== "" && v != null)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");
    api.get(`/audit-log?${params}`).then((r) => setData(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));

  const exportPdf = async () => {
    const params = Object.entries(filters)
      .filter(([k, v]) => v !== "" && v != null && k !== "page" && k !== "page_size")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");
    try {
      // Use Bearer token via api.js so this works even if the auth cookie is gone
      const res = await api.get(`/audit-log/export${params ? `?${params}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank", "noopener");
      // Revoke after the new tab has had time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!w) {
        // Pop-up blocked → fall back to direct nav (cookie auth)
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/audit-log/export${params ? `?${params}` : ""}`;
      }
    } catch (e) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/audit-log/export${params ? `?${params}` : ""}`, "_blank", "noopener");
    }
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) =>
    !["page", "page_size"].includes(k) && v !== "" && v != null
  ).length;

  return (
    <Layout
      title="Cross-suite audit log"
      subtitle="Admin · every action across the platform"
      right={
        <button
          onClick={exportPdf}
          className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
          data-testid="audit-export-pdf-btn"
          title="Open print-ready report. Use the browser's Save as PDF to archive."
        >
          <Download size={14} /> Export log
        </button>
      }
    >
      <div className="space-y-4" data-testid="audit-root">
        {/* Filter strip */}
        <div className="pm-card p-4" data-testid="audit-filters">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Filter size={14} className="text-slate-400" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Filters</span>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setFilters(DEFAULT_FILTERS)}
                className="ml-auto text-[11px] font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                data-testid="audit-clear-filters"
              ><X size={11} /> Clear all ({activeFilterCount})</button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
            <input
              className="pm-input text-xs" placeholder="Search summary / actor…"
              value={filters.q || ""} onChange={(e) => setF({ q: e.target.value })}
              data-testid="audit-search"
            />
            <select className="pm-input text-xs" value={filters.action || ""} onChange={(e) => setF({ action: e.target.value })} data-testid="audit-action-select">
              <option value="">All actions</option>
              {(data.facets?.actions || []).map((a) => (
                <option key={a.value} value={a.value}>{a.value.replace(/_/g, " ")} ({a.count})</option>
              ))}
            </select>
            <select className="pm-input text-xs" value={filters.actor_role || ""} onChange={(e) => setF({ actor_role: e.target.value })} data-testid="audit-role-select">
              <option value="">All roles</option>
              {(data.facets?.roles || []).map((r) => (
                <option key={r.value} value={r.value}>{r.value} ({r.count})</option>
              ))}
            </select>
            <input
              className="pm-input text-xs" placeholder="Record type (contract / case / …)"
              value={filters.record_type || ""} onChange={(e) => setF({ record_type: e.target.value })}
              data-testid="audit-record-type"
            />
            <DateFilter
              label="From"
              value={filters.date_from}
              onChange={(v) => setF({ date_from: v })}
              testid="audit-date-from"
            />
            <DateFilter
              label="To"
              value={filters.date_to}
              onChange={(v) => setF({ date_to: v })}
              testid="audit-date-to"
            />
          </div>
          <DateRangePresets
            onPreset={(from, to) => setF({ date_from: from, date_to: to })}
          />
          <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500" data-testid="audit-results-meta">
            {data.total} total · page {data.page} of {data.total_pages}
            <span className="ml-auto pm-pill pm-pill-amber text-[10px]">ADMIN ONLY</span>
          </div>
        </div>

        {/* List */}
        <div className="pm-card divide-y divide-slate-100" data-testid="audit-list">
          {data.items.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="audit-empty">No audit entries match this filter.</div>}
          {data.items.map((e) => (
            <div key={e.id} className="p-4 flex items-start gap-3" data-testid={`audit-${e.id}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${e.actor_role === "system" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                {e.actor_role === "system" ? <ShieldCheck size={14} /> : <Activity size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{e.actor}</span>
                  <span className="pm-pill pm-pill-slate text-[10px]">{e.actor_role}</span>
                  <span className={`pm-pill text-[10px] ${ACTION_TONE[e.action] || "pm-pill-slate"}`}>{e.action.replace(/_/g, " ")}</span>
                  {e.ip && <span className="pm-pill pm-pill-slate text-[10px]" title="Origin IP" data-testid={`audit-ip-${e.id}`}>{e.ip}</span>}
                </div>
                <div className="text-[13px] text-slate-700 mt-0.5">{e.summary}</div>
                {(e.record_type || e.device) && (
                  <div className="text-[10px] text-slate-400 mt-0.5 font-mono-tab truncate">
                    {e.record_type && <>{e.record_type} · {e.record_id?.slice(0, 8) || "—"} · </>}
                    {e.device && <span title={e.device}>{e.device.slice(0, 70)}</span>}
                  </div>
                )}
              </div>
              <span className="text-[10px] text-slate-400 font-mono-tab shrink-0">{timeAgo(e.created_at)}</span>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-1" data-testid="audit-pagination">
          <button
            disabled={data.page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-40"
            data-testid="audit-page-prev"
          ><ChevronLeft size={12} /> Previous</button>
          <span className="text-[11px] text-slate-500 font-mono-tab">Showing {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} of {data.total}</span>
          <button
            disabled={data.page >= data.total_pages}
            onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-40"
            data-testid="audit-page-next"
          >Next <ChevronRight size={12} /></button>
        </div>
      </div>
    </Layout>
  );
}

/* --------- Reusable date filter w/ custom popover calendar (closes A007/A008) --------- */
function DateFilter({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const display = value ? new Date(value + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="pm-input text-xs w-full pr-6 text-left inline-flex items-center justify-between relative"
          aria-label={label}
          data-testid={testid}
        >
          <span className={display ? "text-slate-900" : "text-slate-400"}>
            {display || label}
          </span>
          <CalendarIcon size={11} className="text-slate-400 ml-1.5 shrink-0" />
          {value && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute right-6 text-slate-400 hover:text-slate-700 rounded p-0.5"
              aria-label={`Clear ${label}`}
              data-testid={`${testid}-clear`}
            >
              <X size={10} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? new Date(value + "T00:00:00") : undefined}
          onSelect={(d) => {
            if (!d) { onChange(""); setOpen(false); return; }
            const iso = d.toISOString().slice(0, 10);
            onChange(iso);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function DateRangePresets({ onPreset }) {
  const setRange = (daysBack) => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - daysBack);
    onPreset(from.toISOString().slice(0, 10), today.toISOString().slice(0, 10));
  };
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="audit-date-presets">
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mr-1">Quick range</span>
      <PresetChip onClick={() => setRange(0)} label="Today" testid="audit-preset-today" />
      <PresetChip onClick={() => setRange(7)} label="7d" testid="audit-preset-7d" />
      <PresetChip onClick={() => setRange(30)} label="30d" testid="audit-preset-30d" />
      <PresetChip onClick={() => setRange(90)} label="90d" testid="audit-preset-90d" />
    </div>
  );
}

function PresetChip({ onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-slate-700"
      data-testid={testid}
    >
      {label}
    </button>
  );
}
